import { STORAGE_KEYS } from '~/utils/storage'

export type LikePersistenceType = 'content' | 'album'

const LIKE_STORAGE_KEYS: Record<LikePersistenceType, string> = {
  content: STORAGE_KEYS.LIKE_CONTENT,
  album: STORAGE_KEYS.LIKE_ALBUM
}

const normalizeLikeId = (id: number | string | undefined | null) => {
  const value = Number(id || 0)
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0
}

const readLikedRecord = (key: string) => {
  const record: Record<number, boolean> = {}
  if (!import.meta.client) {
    return record
  }

  try {
    const raw = localStorage.getItem(key)
    const parsed = raw ? JSON.parse(raw) : []
    if (!Array.isArray(parsed)) {
      return record
    }
    parsed.forEach(item => {
      const id = normalizeLikeId(item)
      if (id > 0) {
        record[id] = true
      }
    })
  } catch {
    localStorage.removeItem(key)
  }

  return record
}

const writeLikedRecord = (key: string, record: Record<number, boolean>) => {
  if (!import.meta.client) {
    return
  }

  try {
    const ids = Object.keys(record)
      .map(id => normalizeLikeId(id))
      .filter(id => id > 0)
    localStorage.setItem(key, JSON.stringify(ids))
  } catch {
    // Ignore storage quota/private-mode failures; backend dedup still protects the count.
  }
}

export const useLikePersistence = (type: LikePersistenceType) => {
  const key = LIKE_STORAGE_KEYS[type]
  const likedRecord = useState<Record<number, boolean>>(`blog-like-persistence:${type}`, () => ({}))
  const hydrated = useState<boolean>(`blog-like-persistence-hydrated:${type}`, () => false)

  const hydrate = () => {
    if (!import.meta.client || hydrated.value) {
      return
    }
    likedRecord.value = readLikedRecord(key)
    hydrated.value = true
  }

  const isLiked = (id: number | string | undefined | null) => {
    hydrate()
    const normalizedId = normalizeLikeId(id)
    return normalizedId > 0 && likedRecord.value[normalizedId] === true
  }

  const markLiked = (id: number | string | undefined | null) => {
    hydrate()
    const normalizedId = normalizeLikeId(id)
    if (normalizedId <= 0) {
      return
    }

    likedRecord.value = {
      ...likedRecord.value,
      [normalizedId]: true
    }
    writeLikedRecord(key, likedRecord.value)
  }

  return {
    hydrate,
    isLiked,
    markLiked
  }
}
